import { useMemo } from 'react'
import { EventModalContext } from './context'
import { EventModalControllerProps } from './types'
import { useSnackbar } from '@contexts/Snackbar'
import {
  userEventCreateAPI,
  userEventDeleteAPI,
} from '@services/nomos_api/resources/event/user_events'
import { AxiosErrorResponse } from '@services/nomos_api/entities/axios'

export function EventModalController({
  children,
  event,
}: EventModalControllerProps): JSX.Element {
  const { savedPropositions } = event
  const { showSnackbarSuccess, showSnackbarError } = useSnackbar()

  const countSavedPropositions = useMemo((): number => {
    return (savedPropositions || []).reduce<Map<string, number>>(
      (counter, item) => {
        counter.set(
          item.propositionId,
          (counter.get(item.propositionId) || 0) + 1
        )
        return counter
      },
      new Map<string, number>()
    ).size
  }, [savedPropositions])

  const handleSaveEvent = async (): Promise<void> => {
    try {
      await userEventCreateAPI(event.id)
      showSnackbarSuccess(
        'Evento salvo na agenda',
        'O evento foi salvo com sucesso.'
      )
      window.location.reload()
    } catch (error) {
      const e = error as AxiosErrorResponse
      showSnackbarError(
        'Erro ao salvar evento',
        e?.response?.data?.message ||
          'Ocorreu um erro ao tentar salvar o evento.'
      )
      throw error
    }
  }

  const handleMonitorEvent = (): void => {
    window.location.href = `/events/${event?.id}?openMonitoring=true`
  }

  const handleSaveOrMonitorEvent = async (
    hasSavedEvent: boolean
  ): Promise<void> => {
    try {
      if (hasSavedEvent) {
        await handleSaveEvent()
      } else {
        handleMonitorEvent()
      }
    } catch (error) {
      const e = error as AxiosErrorResponse
      showSnackbarError(
        'Erro ao salvar ou monitorar evento',
        e?.response?.data?.message ||
          'Ocorreu um erro ao tentar salvar ou monitorar o evento.'
      )
      throw error
    }
  }

  const handleRemoveEvent = async (): Promise<void> => {
    const eventId = event.userEvents?.[0]?._id
    try {
      if (!eventId) {
        throw new Error('ID do evento não encontrado.')
      }

      await userEventDeleteAPI(eventId)
      showSnackbarSuccess(
        'Evento removido da agenda',
        'O evento foi removido com sucesso.'
      )
      window.location.reload()
    } catch (error) {
      const e = error as AxiosErrorResponse
      showSnackbarError(
        'Erro ao remover evento',
        e?.response?.data?.message ||
          'Ocorreu um erro ao tentar remover o evento.'
      )
      throw error
    }
  }

  const state = useMemo(
    () => ({
      countSavedPropositions,
      handleSaveEvent,
      handleRemoveEvent,
      handleMonitorEvent,
      handleSaveOrMonitorEvent,
    }),
    [countSavedPropositions, event]
  )

  return (
    <EventModalContext.Provider value={state}>
      {children}
    </EventModalContext.Provider>
  )
}
